/**
 * Maps backend error reasons and messages to professional, user-friendly Instacart-style error messages
 */

export interface ReceiptError {
  reason?: string;
  error?: string;
  message?: string;
}

export function getReceiptErrorMessage(error: ReceiptError | string): string {
  // If it's a string error message, check its content
  const errorText = typeof error === 'string' ? error : (error.error || error.message || '');
  const reason = typeof error === 'object' ? error.reason : '';

  // 1. Blurry images or unreadable content
  if (
    reason === 'no_ai_content' ||
    reason === 'json_parse_error' ||
    errorText.toLowerCase().includes('blurry') ||
    errorText.toLowerCase().includes('not detected') ||
    errorText.toLowerCase().includes('edges not')
  ) {
    return 'Image too blurry — please retake the photo.';
  }

  // 2. Lighting issues
  if (
    errorText.toLowerCase().includes('lighting') ||
    errorText.toLowerCase().includes('too dark') ||
    errorText.toLowerCase().includes('overexposed')
  ) {
    return 'Lighting issue — retake the photo with better lighting.';
  }

  // 3. Receipt not fully in frame
  if (
    errorText.toLowerCase().includes('receipt edges') ||
    errorText.toLowerCase().includes('align the receipt') ||
    errorText.toLowerCase().includes('not fully')
  ) {
    return 'Receipt edges not detected — align the receipt inside the frame and retake.';
  }

  // 4. Server timeout
  if (
    reason === 'server_timeout' ||
    errorText.toLowerCase().includes('timeout') ||
    errorText.toLowerCase().includes('timed out')
  ) {
    return 'Server timeout — tap retry.';
  }

  // 5. File too large
  if (
    reason === 'file_too_large' ||
    errorText.toLowerCase().includes('too large') ||
    errorText.toLowerCase().includes('file size')
  ) {
    return 'File too large — upload a smaller image.';
  }

  // 6. Invalid file type
  if (
    reason === 'invalid_file_type' ||
    reason === 'invalid_image' ||
    errorText.toLowerCase().includes('unsupported format') ||
    errorText.toLowerCase().includes('invalid file type') ||
    errorText.toLowerCase().includes('file type')
  ) {
    return 'Unsupported format — upload JPG, PNG or PDF.';
  }

  // 7. Rate limit (only when truly reached)
  if (
    reason === 'rate_limited' ||
    reason === 'ai_rate_limited' ||
    errorText.toLowerCase().includes('rate limit')
  ) {
    return 'Upload limit reached — try again shortly.';
  }

  // 8. Network/connection issues
  if (
    reason === 'network_error' ||
    reason === 'download_error' ||
    errorText.toLowerCase().includes('network') ||
    errorText.toLowerCase().includes('connection') ||
    errorText.toLowerCase().includes('failed to fetch')
  ) {
    return 'Connection issue — check your internet and retry.';
  }

  // 9. Storage/file access errors
  if (
    reason === 'storage_error' ||
    reason === 'file_not_found' ||
    errorText.toLowerCase().includes('storage') ||
    errorText.toLowerCase().includes('file not found')
  ) {
    return 'Upload failed — please try again.';
  }

  // 10. Authentication errors
  if (
    reason === 'no_auth_header' ||
    reason === 'invalid_token' ||
    errorText.toLowerCase().includes('unauthorized') ||
    errorText.toLowerCase().includes('not authenticated')
  ) {
    return 'Session expired — please sign in again.';
  }

  // 11. AI credits exhausted
  if (
    reason === 'credits_exhausted' ||
    errorText.toLowerCase().includes('credits exhausted') ||
    errorText.toLowerCase().includes('payment required')
  ) {
    return 'Service temporarily unavailable — please try again later.';
  }

  // Generic fallback for any other error
  return 'Upload failed — please try again.';
}
